import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import { directoryHref } from "@/lib/atwDirectoryHost";

type AuditEvent = {
  id: string;
  action: string;
  description: string;
  actorName?: string | null;
  createdAt: string;
  recordId?: string | null;
};

export function SecurityPage() {
  const state = useDirectoryState();
  const ctx = state.status === "ready" ? state.value : null;
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "audit" ? "audit" : "policy";
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [pendingInvites, setPendingInvites] = useState<number | null>(null);
  const [pendingRequests, setPendingRequests] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSaving, setMfaSaving] = useState(false);

  const load = useCallback(async () => {
    if (!ctx?.canManageUsers || !ctx.org?.domain) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMfaRequired(ctx.org.mfaRequired === true);
    try {
      const [auditRes, usersRes, reqRes] = await Promise.all([
        fetch(apiUrl("/api/audit?q=directory&limit=50"), { ...apiFetchInit(), cache: "no-store" }),
        fetch(apiUrl(`/api/users?accountDomain=${encodeURIComponent(ctx.org.domain)}&limit=100&skip=0`), {
          ...apiFetchInit(),
          cache: "no-store",
        }),
        fetch(apiUrl("/api/directory/access-request?status=pending"), {
          ...apiFetchInit(),
          cache: "no-store",
        }),
      ]);

      if (auditRes.ok) {
        const data = await auditRes.json();
        setEvents((data.events || []) as AuditEvent[]);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        const users = (data.users || []) as Array<{
          verificationState?: string;
        }>;
        setPendingInvites(
          users.filter(
            (u) =>
              u.verificationState === "invite_pending" || u.verificationState === "missing_email",
          ).length,
        );
      }

      if (reqRes.ok) {
        const data = await reqRes.json();
        setPendingRequests((data.requests || []).length);
      }
    } catch {
      toast.error("Could not load security summary.");
    } finally {
      setLoading(false);
    }
  }, [ctx?.canManageUsers, ctx?.org?.domain, ctx?.org?.mfaRequired]);

  useEffect(() => {
    if (ctx) void load();
  }, [ctx, load]);

  if (!ctx) return null;

  if (!ctx.canManageUsers) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Security</h1>
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
          Security policy and audit live here. Your role in this organization is not Organization
          Owner or Account Admin, so those tools stay locked. Check the account menu for the active
          org.
        </p>
      </div>
    );
  }

  const saveMfa = async (next: boolean) => {
    if (!ctx.org?.domain) return;
    setMfaSaving(true);
    setMfaRequired(next);
    try {
      const res = await fetch(apiUrl(`/api/accounts/${encodeURIComponent(ctx.org.domain)}`), {
        ...apiFetchInit(),
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfaRequired: next }),
      });
      const data = (await res.json()) as { error?: string; mfaPolicyNote?: string };
      if (!res.ok) {
        setMfaRequired(!next);
        toast.error(data.error || "Could not update MFA policy.");
        return;
      }
      toast.success(
        data.mfaPolicyNote
          ? `MFA policy saved. ${data.mfaPolicyNote}`
          : next
            ? "MFA required for this organization."
            : "MFA requirement turned off.",
      );
      if (state.status === "ready") state.reload();
    } catch {
      setMfaRequired(!next);
      toast.error("Could not update MFA policy.");
    } finally {
      setMfaSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {tab === "audit" ? "Directory audit" : "Security policy"}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {tab === "audit"
            ? `Identity and access changes recorded for ${ctx.org?.companyName || ctx.org?.domain || "your organization"}.`
            : `Access hygiene for ${ctx.org?.companyName || ctx.org?.domain || "your organization"}: org MFA, invites, and open requests.`}
        </p>
      </div>

      {tab === "policy" ? (
        <>
          <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-[#1D232D]">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Require MFA</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Prefer authenticator TOTP (or SMS when enabled). For ATW Mail orgs, avoid email OTP until
              the mailbox exists. When this organization has a dedicated Cognito user pool, turning MFA
              on also sets pool MFA to required.
            </p>
            {ctx.org?.domain ? (
              <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-gray-200 px-3 py-3 dark:border-white/15">
                <div>
                  <Label htmlFor="dir-mfa-required" className="text-sm font-medium">
                    Organization-wide MFA
                  </Label>
                  <p className="text-xs text-gray-500">
                    Same setting as Company settings. Reset MFA for a person from their profile.
                  </p>
                </div>
                <Switch
                  id="dir-mfa-required"
                  checked={mfaRequired}
                  disabled={mfaSaving}
                  onCheckedChange={(v) => void saveMfa(v)}
                />
              </div>
            ) : null}
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <Link
              to={directoryHref("/people?invite=invite_pending")}
              className="rounded-xl border border-gray-200 bg-white p-4 hover:bg-gray-50 dark:border-white/10 dark:bg-[#1D232D] dark:hover:bg-white/5"
            >
              <p className="text-sm text-gray-500">Pending invites</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {loading && pendingInvites === null ? "—" : (pendingInvites ?? 0)}
              </p>
            </Link>
            <Link
              to={directoryHref("/requests")}
              className="rounded-xl border border-gray-200 bg-white p-4 hover:bg-gray-50 dark:border-white/10 dark:bg-[#1D232D] dark:hover:bg-white/5"
            >
              <p className="text-sm text-gray-500">Open access requests</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {loading && pendingRequests === null ? "—" : (pendingRequests ?? 0)}
              </p>
            </Link>
          </section>
        </>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-[#1D232D]">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-white/10">
            <h2 className="font-semibold text-gray-900 dark:text-white">Recent Directory audit</h2>
            <p className="text-sm text-gray-500">Identity and access changes recorded for this org.</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
            </div>
          ) : events.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-gray-500">No Directory audit events yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-white/10">
              {events.map((event) => (
                <li key={event.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{event.action}</Badge>
                    <span className="text-xs text-gray-400">
                      {new Date(event.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">{event.description}</p>
                  {event.actorName ? (
                    <p className="text-xs text-gray-500">by {event.actorName}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
